import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { CP, DOMAIN_COLORS, CapCard, Pill } from "./ui";

const STRENGTH_STYLE = { strong: 3.5, moderate: 2, weak: 1 };

export default function SkillDetail() {
  const [catalog, setCatalog] = useState([]);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get("/capability/skills/catalog").then(({ data }) => {
      setCatalog(data.skills || []);
      if (data.skills?.length) load(data.skills[0].skill);
    }).catch(() => {});
  }, []);

  const load = (skill) => {
    setOpen(false);
    setQuery("");
    api.get(`/capability/skill/${encodeURIComponent(skill)}`).then(({ data }) => setDetail(data)).catch(() => {});
  };

  const matches = useMemo(() =>
    catalog.filter((s) => s.skill.includes(query.toLowerCase())).slice(0, 12), [catalog, query]);

  const d = detail;
  const dist = d?.proficiency_distribution || {};
  const distTotal = Math.max(1, Object.values(dist).reduce((a, b) => a + b, 0));

  return (
    <div className="space-y-4" data-testid="skill-detail">
      <div className="relative max-w-md">
        <input value={query} onFocus={() => setOpen(true)} onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          placeholder={d ? `Selected: ${d.skill} — search another…` : "Search a skill…"}
          className="w-full h-10 px-3 rounded-lg text-sm bg-white outline-none"
          style={{ border: `1px solid ${CP.border}`, color: CP.text }} data-testid="skill-picker" />
        {open && matches.length > 0 && (
          <div className="absolute z-30 top-full left-0 right-0 mt-1 rounded-lg bg-white shadow-lg max-h-64 overflow-auto" style={{ border: `1px solid ${CP.border}` }}>
            {matches.map((s) => (
              <button key={s.skill} onClick={() => load(s.skill)} className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between" data-testid={`skill-option-${s.skill}`}>
                <span className="text-sm capitalize" style={{ color: CP.text }}>{s.skill}</span>
                <span className="text-[10px]" style={{ color: CP.sub }}>{s.supply} people · {s.open_demand} open</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {d && (
        <>
          <div className="rounded-xl bg-white shadow-sm p-5" style={{ border: `1px solid ${CP.border}` }} data-testid="skill-header">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-xl font-bold capitalize" style={{ color: CP.text }}>{d.skill}</h2>
              {d.core && <Pill tone="purple">Core Capability</Pill>}
              <span className="text-xs px-2 py-0.5 rounded-full capitalize" style={{ background: "#F1F5F9", color: CP.sub }}>{d.domain}</span>
            </div>
            <p className="text-sm mt-1.5 max-w-2xl" style={{ color: CP.sub }}>{d.description}</p>
            <div className="flex flex-wrap gap-4 mt-3 text-sm">
              <span>Demand <Pill tone={d.demand_label === "High" ? "red" : d.demand_label === "Low" ? "green" : "amber"}>{d.demand_label}</Pill></span>
              <span>Supply <Pill tone={d.supply_label === "High" ? "green" : d.supply_label === "Low" ? "red" : "amber"}>{d.supply_label}</Pill></span>
              <span>Growth <span className="font-bold" style={{ color: d.growth_pct >= 0 ? CP.green : CP.red }}>{d.growth_pct >= 0 ? "+" : ""}{d.growth_pct}%</span></span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <CapCard title="Adjacent Skills" subtitle="Co-occurrence strength in real work" testid="skill-adjacent">
              <div className="relative mx-auto" style={{ width: 340, height: 320 }}>
                <svg width={340} height={320}>
                  {(d.adjacent || []).map((a, i) => {
                    const angle = (2 * Math.PI * i) / Math.max(d.adjacent.length, 1) - Math.PI / 2;
                    const x = 170 + Math.cos(angle) * 115;
                    const y = 155 + Math.sin(angle) * 110;
                    return (
                      <line key={a.skill} x1={170} y1={155} x2={x} y2={y}
                        stroke="#C7D2E8" strokeWidth={STRENGTH_STYLE[a.strength]} />
                    );
                  })}
                </svg>
                <CenterNode label={d.skill} />
                {(d.adjacent || []).map((a, i) => {
                  const angle = (2 * Math.PI * i) / Math.max(d.adjacent.length, 1) - Math.PI / 2;
                  const x = 170 + Math.cos(angle) * 115;
                  const y = 155 + Math.sin(angle) * 110;
                  return (
                    <button key={a.skill} onClick={() => load(a.skill)} data-testid={`adjacent-node-${a.skill}`}
                      className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full text-[10px] font-semibold px-2.5 py-1.5 capitalize shadow-sm hover:scale-105 transition-transform"
                      style={{ left: x, top: y, background: "#fff", border: `2px solid ${DOMAIN_COLORS[d.domain] || CP.blue}`, color: CP.text, maxWidth: 110 }}>
                      {a.skill}
                    </button>
                  );
                })}
              </div>
              <div className="flex justify-center gap-4 text-[11px]" style={{ color: CP.sub }}>
                <span className="flex items-center gap-1"><span className="inline-block w-6" style={{ borderTop: "3.5px solid #C7D2E8" }} /> Strong</span>
                <span className="flex items-center gap-1"><span className="inline-block w-6" style={{ borderTop: "2px solid #C7D2E8" }} /> Moderate</span>
                <span className="flex items-center gap-1"><span className="inline-block w-6" style={{ borderTop: "1px solid #C7D2E8" }} /> Weak</span>
              </div>
            </CapCard>

            <CapCard title="Skill Details" testid="skill-details-panel">
              <div className="space-y-4">
                <div>
                  <div className="text-xs font-semibold mb-2" style={{ color: CP.sub }}>Proficiency Levels</div>
                  {["beginner", "intermediate", "advanced", "expert"].map((lvl) => (
                    <div key={lvl} className="flex items-center gap-2 mb-1.5">
                      <span className="w-24 text-xs capitalize" style={{ color: CP.text }}>{lvl}</span>
                      <div className="flex-1 h-2 rounded-full" style={{ background: "#EDF1F9" }}>
                        <div className="h-full rounded-full" style={{ width: `${((dist[lvl] || 0) / distTotal) * 100}%`, background: CP.blue }} />
                      </div>
                      <span className="w-10 text-right text-xs font-semibold" style={{ color: CP.text }}>{dist[lvl] || 0}</span>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <MiniStat label="People with Skill" value={d.people_count.toLocaleString()} />
                  <MiniStat label="Open Demand" value={d.open_demand} />
                  <MiniStat label="Avg Proficiency" value={`${d.avg_prof}/5`} />
                </div>
                <div>
                  <div className="text-xs font-semibold mb-1.5" style={{ color: CP.sub }}>Top Departments</div>
                  <div className="flex flex-wrap gap-1.5">
                    {(d.top_departments || []).map((t) => (
                      <span key={t.name} className="text-[11px] px-2 py-1 rounded-full" style={{ background: "#EFF4FF", color: CP.blue }}>{t.name} ({t.count})</span>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-semibold mb-1.5" style={{ color: CP.sub }}>Top Practitioners</div>
                  <div className="space-y-1">
                    {(d.top_experts || []).map((e) => (
                      <div key={e.user_id} className="flex justify-between text-xs">
                        <span style={{ color: CP.text }}>{e.name}</span>
                        <span style={{ color: CP.sub }}>{e.evidence} tasks</span>
                      </div>
                    ))}
                    {(d.top_experts || []).length === 0 && <div className="text-xs" style={{ color: CP.sub }}>No completed evidence yet.</div>}
                  </div>
                </div>
              </div>
            </CapCard>
          </div>
        </>
      )}
    </div>
  );
}

function CenterNode({ label }) {
  return (
    <div className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full flex items-center justify-center text-white text-[11px] font-bold capitalize text-center shadow-lg"
      style={{ left: 170, top: 155, width: 84, height: 84, background: CP.blue, border: "4px solid #BFD3FE", padding: 6 }}>
      {label}
    </div>
  );
}
function MiniStat({ label, value }) {
  return (
    <div className="rounded-lg p-2" style={{ border: `1px solid ${CP.border}` }}>
      <div className="text-base font-bold" style={{ color: CP.navy }}>{value}</div>
      <div className="text-[10px]" style={{ color: CP.sub }}>{label}</div>
    </div>
  );
}
